<template>
  <div>
    <div class="header-bg"></div>
    <div class="max-w-screen-2xl mx-auto mt-10 pb-16 px-5 sm:px-10">
      <form @submit.prevent="readyToSearch">
        <div class="md:flex md:flex-row md:flex-auto md:gap-3 lg:gap-5">
          <div
            v-for="(section, sectionIndex) in formInputs"
            :key="sectionIndex"
            :class="section.sectionName === 'map' ? 'flex-grow' : 'flex-none'"
          >
            <div v-for="(input, index) in section.inputs" :key="index">
              <!-- title -->
              <div v-if="input.type === 'title'" class="field-title">
                <div class="">{{ input.label }}</div>
              </div>

              <!-- text -->
              <div
                v-else-if="input.type === 'text'"
                class="w-full md:w-44 lg:w-64"
              >
                <input
                  :id="input.field"
                  :name="input.field"
                  :placeholder="input.label"
                  type="text"
                  class="field-text"
                  v-model="fields[input.field]"
                />
              </div>

              <!-- boolean -->
              <div
                v-else-if="input.type === 'boolean'"
                class="text-left space-x-2 py-2"
              >
                <input
                  :id="input.field"
                  :name="input.field"
                  :placeholder="input.label"
                  type="checkbox"
                  v-model="fields[input.field]"
                />
                <label class="field-checkbox">{{ input.label }}</label>
              </div>

              <!-- boolean group -->
              <div
                v-else-if="input.type === 'boolean-group'"
                class="flex flex-wrap gap-x-5 gap-y-0 w-full md:w-44 lg:w-64 mb-3"
              >
                <div
                  v-for="(item, itemIndex) in input.items"
                  :key="itemIndex"
                  class="space-x-2"
                >
                  <input
                    :id="item.field"
                    :name="item.field"
                    type="checkbox"
                    v-model="fields[item.field]"
                  />
                  <label class="field-checkbox">{{ item.label }}</label>
                </div>
              </div>

              <!-- menu -->
              <div v-else-if="input.type === 'menu'">
                <select class="field-menu" v-model="fields[input.field]">
                  <option
                    v-for="country in countries"
                    :key="country.key"
                    :value="country.key"
                  >
                    {{ country.value }}
                  </option>
                </select>
              </div>

              <!-- map -->
              <div v-else-if="input.type === 'map'">
                <div class="border rounded-md border-gray-300">
                  <DataMap ref="dataMap" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr class="mt-5 mb-5">

        <!-- search buttons -->
        <div class="dialog-footer">
          <button class="search-button text-2xl font-bold" type="submit">
            搜&nbsp;&nbsp;尋
          </button>
          <button class="cancel-button" @click="backToSearchPage" type="button">
            返回
          </button>
          <button class="reset-button" @click="reset" type="button">
            重設
          </button>
        </div>
      </form>
    </div>
    <div class="advanced-footer-bg"></div>
  </div>
</template>

<script>
import Utils from "@/utils/index";
import DataMap from "@/views/search/DataMap.vue";

export default {
  components: {
    DataMap,
  },

  data() {
    return {
      countries: Utils.countries(),

      // fromInputs: 進階搜尋欄位結構
      // * sectionName: 區塊名稱，區分用不顯示
      // * inputs: 輸入欄位的陣列，包含以下屬性：
      //   - label: 欄位說明
      //   - type: 輸入欄位類型，包含 title, text, boolean, boolean-group
      //   - field: 對應 v-model 的名稱
      formInputs: [
        {
          sectionName: "section1",
          inputs: [
            { label: "物種分類", type: "title" },
            {
              label: "Taxon Name",
              type: "text",
              field: "latin_name",
            },
            { label: "Family", type: "text", field: "family" },
            { label: "Genus", type: "text", field: "genus" },
            {
              label: "Specific Epithet",
              type: "text",
              field: "specific_epithet",
            },
            {
              label: "Infraspecific Epithet",
              type: "text",
              field: "infraspecific_epithet",
            },
            {
              label: "包含異名與接受名",
              type: "boolean",
              field: "is_accept_synonym",
            },
            { label: "中名", type: "text", field: "td_chinese_name" },

            { label: "標本資訊", type: "title" },
            { label: "館號", type: "text", field: "catalog_number" },
            { label: "鑑定者", type: "text", field: "identified_by" },
            {
              type: "boolean-group",
              items: [
                {
                  label: "模式標本",
                  type: "boolean",
                  field: "search_with_type",
                },
                {
                  label: "野生植株",
                  type: "boolean",
                  field: "search_only_wild",
                },
                {
                  label: "具有影像",
                  type: "boolean",
                  field: "includeImage",
                },
                { label: "具有座標", type: "boolean", field: "include_gps" },
              ],
            }
          ],
        },
        {
          sectionName: "section2",
          inputs: [
            { label: "地理位置", type: "title" },
            {
              label: "國家",
              type: "menu",
              field: "country_code",
            },
            { label: "不含台灣", type: "boolean", field: "not_include_taiwan" },
            { label: "州省", type: "text", field: "state_tw" },
            { label: "縣市", type: "text", field: "county" },
            { label: "鄉鎮", type: "text", field: "township" },
            { label: "採集地點", type: "text", field: "verbatim_locality" },

            { label: "採集資訊", type: "title" },
            { label: "採集者", type: "text", field: "recorded_by" },
            { label: "採集編號", type: "text", field: "record_number" },
            { label: "採集年", type: "text", field: "event_year" },
            { label: "採集月", type: "text", field: "event_month" },
            { label: "採集日", type: "text", field: "event_day" },
          ],
        },
        {
          sectionName: "map",
          inputs: [
            { label: "區域框選", type: "title" },
            { label: "地圖", type: "map" },
          ],
        },
      ],

      fields: {
        latin_name: "",
        family: "",
        genus: "",
        specific_epithet: "",
        infraspecific_epithet: "",
        is_accept_synonym: false,
        td_chinese_name: "",
        catalog_number: "",
        search_with_type: false,
        search_only_wild: false,
        includeImage: false, // 轉換成 !include_no_img
        include_gps: false,
        country_code: "WORLD",
        not_include_taiwan: false,
        state_tw: "",
        county: "",
        township: "",
        verbatim_locality: "",
        recorded_by: "",
        record_number: "",
        event_year: "",
        event_month: "",
        event_day: "",
      },
    };
  },

  mounted() {
    // 在 UI 顯示搜尋欄位值
    for (const [key, value] of Object.entries(
      this.$store.state.search.query.fields
    )) {
      if (value !== "") {
        this.fields[key] = value;
      }
    }
  },

  methods: {
    reset() {
      // 清除搜尋條件
      this.fields = {
        country_code: "WORLD",
      };
      this.$store.commit("search/resetFields");

      // 清除地圖框選範圍
      this.$refs.dataMap.resetVertexes();
    },

    // 檢查全部的輸入欄位值是否有值
    noFields() {
      let noFields = true;

      for (const element of Object.entries(this.fields)) {
        if (
          (typeof element[1] === "string" && element[1] !== "") ||
          (typeof element[1] === "boolean" && element[1] === true)
        ) {
          noFields = false;
        }
      }

      return noFields;
    },

    // 檢查地圖是否有框選
    noMapSelection() {
      let noMapSelection = true;

      if (
        (this.$refs.dataMap.isPolygon === true &&
          this.$refs.dataMap.vertexes.length >= 3) ||
        (this.$refs.dataMap.isPolygon === false &&
          this.$refs.dataMap.vertexes.length >= 2)
      ) {
        noMapSelection = false;
      }

      return noMapSelection;
    },

    readyToSearch() {
      if (this.noFields() && this.noMapSelection()) {
        this.$toast.info("請輸入搜尋條件或框選搜尋區域！", { queue: true });
        return;
      }

      // 搜尋後顯示第一頁
      this.$store.commit("search/setPageNumber", 1);

      // 將輸入的搜尋欄位值儲存至 vuex
      this.$store.commit("search/setFields", this.fields);

      // 將地圖框選的節點儲存至 vuex
      this.$refs.dataMap.setVertexes();

      // 跳轉至搜尋頁面
      this.$router.push("/search");
    },

    backToSearchPage() {
      this.$router.push("/search");
    },
  },
};
</script>

<style>
.dialog {
  /* @apply fixed w-5/6 top-5 z-50 border border-gray-400 rounded-lg bg-gray-50 flex flex-col justify-center shadow-lg; */
  @apply w-5/6 border rounded-lg bg-gray-50 flex flex-col justify-center shadow-lg;
}
.dialog-header {
  @apply flex justify-between mb-3 bg-gray-100 h-14 p-5 items-center text-gray-700 rounded-t-lg;
}
.dialog-footer {
  @apply flex justify-start mt-5 md:mt-2 space-x-3 items-center text-gray-700;
}
.form {
  @apply flex;
}
.field-title {
  @apply text-lg text-left font-bold mt-5 mb-2;
}
.field-text {
  @apply mb-2 w-full md:w-44 lg:w-64 px-3 py-1 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500;
}
.field-checkbox {
  @apply text-left;
}
.field-menu {
  @apply mb-2 w-full md:w-44 lg:w-64 px-3 py-1 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500;
}
input:checked ~ .dot {
  transform: translateX(100%);
  background-color: #48bb78;
}
.map-button {
  @apply w-8 h-8;
}
.advanced-footer-bg {
  width: 100%;
  height: 10px;
  background-image: url("../../assets/footer-bg-login.svg");
}
.header-bg {
  width: 100%;
  height: 10px;
  background-image: url("../../assets/footer-bg-login.svg");
  transform: scaleY(-1);
}
.search-button {
  color: #fff;
  background-color: #64859d;
  border-radius: 0.25rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
.search-button:hover {
  color: #fff;
  background-color: #405d73;
}

.cancel-button {
  @apply bg-gray-300 rounded px-5 py-2;
}
.cancel-button:hover {
  @apply bg-gray-400 rounded px-5 py-2 text-white;
}
.reset-button {
  color: #fff;
  background-color: #c9a798;
  border-radius: 0.25rem;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.reset-button:hover {
  color: #fff;
  background-color: #a8897c;
}
</style>
